import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getComplianceReports,
  generateComplianceReport,
  deleteComplianceReport,
  ComplianceReport,
} from '../../api/adminClient';
import clsx from 'clsx';

const reportTypeLabels: Record<string, { label: string; description: string; icon: string }> = {
  access_review: {
    label: 'Access Review',
    description: 'Review user permissions, login activity, and access risk',
    icon: '🔐',
  },
  change_summary: {
    label: 'Change Summary',
    description: 'Summary of all changes made in the system',
    icon: '📝',
  },
  login_activity: {
    label: 'Login Activity',
    description: 'Login success/failure rates and suspicious activity',
    icon: '🔑',
  },
};

export default function AdminCompliance() {
  const queryClient = useQueryClient();
  const [selectedReport, setSelectedReport] = useState<ComplianceReport | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingType, setGeneratingType] = useState<string | null>(null);

  const { data: reports, isLoading } = useQuery({
    queryKey: ['admin', 'compliance-reports'],
    queryFn: () => getComplianceReports(),
  });

  const generateMutation = useMutation({
    mutationFn: ({ type, params }: { type: string; params?: Record<string, unknown> }) =>
      generateComplianceReport(type, params),
    onSuccess: (report) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'compliance-reports'] });
      setSelectedReport(report);
      setIsGenerating(false);
      setGeneratingType(null);
    },
    onError: () => {
      setIsGenerating(false);
      setGeneratingType(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteComplianceReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'compliance-reports'] });
      setSelectedReport(null);
    },
  });

  const handleGenerate = (type: string) => {
    setIsGenerating(true);
    setGeneratingType(type);
    generateMutation.mutate({ type, params: { period_days: 30 } });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Compliance Reports</h1>
        <p className="text-gray-500 mt-1">Generate and view compliance reports</p>
      </div>

      {/* Generate Reports */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate New Report</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(reportTypeLabels).map(([type, info]) => (
            <button
              key={type}
              onClick={() => handleGenerate(type)}
              disabled={isGenerating}
              className={clsx(
                'p-4 rounded-lg border text-left transition-colors',
                isGenerating && generatingType === type
                  ? 'border-primary-300 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50'
              )}
            >
              <div className="text-2xl mb-2">{info.icon}</div>
              <div className="font-medium text-gray-900">{info.label}</div>
              <div className="text-sm text-gray-500 mt-1">{info.description}</div>
              {isGenerating && generatingType === type && (
                <div className="text-sm text-primary-600 mt-2">Generating...</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Recent Reports */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Reports</h2>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : reports?.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No reports generated yet</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Report Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Generated
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reports?.map((report) => {
                const typeInfo = reportTypeLabels[report.report_type] || {
                  label: report.report_type,
                  icon: '📄',
                };
                return (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{typeInfo.icon}</span>
                        <span className="font-medium text-gray-900">{typeInfo.label}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(report.period_start).toLocaleDateString()} -{' '}
                      {new Date(report.period_end).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={clsx(
                          'px-2 py-1 text-xs font-medium rounded-full',
                          report.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : report.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        )}
                      >
                        {report.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(report.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => setSelectedReport(report)}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Report Detail Modal */}
      {selectedReport && (
        <ReportDetailModal
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
          onDelete={() => {
            if (confirm('Are you sure you want to delete this report?')) {
              deleteMutation.mutate(selectedReport.id);
            }
          }}
        />
      )}
    </div>
  );
}

function ReportDetailModal({
  report,
  onClose,
  onDelete,
}: {
  report: ComplianceReport;
  onClose: () => void;
  onDelete: () => void;
}) {
  const typeInfo = reportTypeLabels[report.report_type] || { label: report.report_type, icon: '📄' };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{typeInfo.icon}</span>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{typeInfo.label}</h2>
              <p className="text-sm text-gray-500">
                {new Date(report.period_start).toLocaleDateString()} -{' '}
                {new Date(report.period_end).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-2">Summary</h3>
            <p className="text-gray-700">{report.summary}</p>
          </div>

          {/* Report Data */}
          {report.report_type === 'access_review' && report.data && (
            <AccessReviewData data={report.data} />
          )}

          {report.report_type === 'change_summary' && report.data && (
            <ChangeSummaryData data={report.data} />
          )}

          {report.report_type === 'login_activity' && report.data && (
            <LoginActivityData data={report.data} />
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-between">
          <button
            onClick={onDelete}
            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            Delete Report
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function AccessReviewData({ data }: { data: Record<string, unknown> }) {
  const stats = data.stats as Record<string, number> | undefined;
  const items = data.items as Array<{
    user_email: string;
    user_name: string;
    roles: string[];
    risk_level: string;
    risk_factors: string[];
    days_since_login: number | null;
  }> | undefined;

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total_users}</div>
            <div className="text-sm text-gray-500">Total Users</div>
          </div>
          <div className="bg-white border border-red-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-600">{stats.high_risk}</div>
            <div className="text-sm text-gray-500">High Risk</div>
          </div>
          <div className="bg-white border border-yellow-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.medium_risk}</div>
            <div className="text-sm text-gray-500">Medium Risk</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-600">{stats.dormant_accounts}</div>
            <div className="text-sm text-gray-500">Dormant</div>
          </div>
        </div>
      )}

      {/* Users Table */}
      {items && items.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">User Risk Assessment</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">User</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Roles</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Risk</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Risk Factors</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.slice(0, 20).map((item, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{item.user_name}</div>
                      <div className="text-sm text-gray-500">{item.user_email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.roles.join(', ') || 'None'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={clsx(
                          'px-2 py-1 text-xs font-medium rounded-full',
                          item.risk_level === 'high'
                            ? 'bg-red-100 text-red-800'
                            : item.risk_level === 'medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        )}
                      >
                        {item.risk_level}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{item.risk_factors.join(', ') || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ChangeSummaryData({ data }: { data: Record<string, unknown> }) {
  const byEntityType = data.by_entity_type as Record<string, Record<string, number>> | undefined;
  const topActors = data.top_actors as Array<{ email: string; actions: number }> | undefined;
  const totalChanges = data.total_changes as number | undefined;

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="text-3xl font-bold text-blue-700">{totalChanges?.toLocaleString()}</div>
        <div className="text-sm text-blue-600">Total Changes</div>
      </div>

      {/* By Entity Type */}
      {byEntityType && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">Changes by Entity Type</h3>
          <div className="space-y-2">
            {Object.entries(byEntityType).map(([type, actions]) => (
              <div key={type} className="bg-gray-50 rounded-lg p-3">
                <div className="font-medium text-gray-900 capitalize mb-2">{type.replace('_', ' ')}</div>
                <div className="flex gap-4 text-sm">
                  {Object.entries(actions).map(([action, count]) => (
                    <span key={action} className="text-gray-600">
                      {action}: <span className="font-medium">{count}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Actors */}
      {topActors && topActors.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">Most Active Users</h3>
          <div className="space-y-2">
            {topActors.map((actor, idx) => (
              <div key={idx} className="flex items-center justify-between py-2">
                <span className="text-gray-700">{actor.email}</span>
                <span className="font-medium text-gray-900">{actor.actions} actions</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LoginActivityData({ data }: { data: Record<string, unknown> }) {
  const stats = data.stats as Record<string, { count: number; unique_users: number }> | undefined;
  const failureRate = data.failure_rate as string | undefined;
  const failedLogins = data.failed_logins_by_user as Array<{ email: string; attempts: number }> | undefined;

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-700">{stats.login?.count || 0}</div>
            <div className="text-sm text-green-600">Successful Logins</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-700">{stats.login_failed?.count || 0}</div>
            <div className="text-sm text-red-600">Failed Attempts</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-700">{failureRate}%</div>
            <div className="text-sm text-gray-600">Failure Rate</div>
          </div>
        </div>
      )}

      {/* Failed Logins by User */}
      {failedLogins && failedLogins.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">Users with Failed Login Attempts</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Failed Attempts</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {failedLogins.map((user, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3 text-gray-700">{user.email}</td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={clsx(
                          'px-2 py-1 text-xs font-medium rounded-full',
                          user.attempts >= 5 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        )}
                      >
                        {user.attempts}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
